import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  commitManualLineup,
  fetchManualLineupEligibleFixtures,
  fetchManualLineupRoster,
  previewRotowireSiteLineup,
} from '../api/index';
import { useConfig } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import './LineupsPage.css';

function formatKickoff(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function formatApiError(err) {
  const d = err?.response?.data;
  if (typeof d?.detail === 'string') return d.detail;
  if (d?.detail?.message) return d.detail.message;
  if (Array.isArray(d?.detail)) {
    return d.detail.map((x) => x.msg || JSON.stringify(x)).join('; ');
  }
  return err?.message || 'Request failed';
}

function sideLabel(side) {
  if (side === 'home') return 'home';
  if (side === 'away') return 'away';
  return side || 'unknown side';
}

function playerNames(players = []) {
  const names = players
    .map((p) => p.rotowire_name || p.sleeper_player_name || p.optic_player_name)
    .filter(Boolean);
  if (names.length <= 5) return names.join(', ');
  return `${names.slice(0, 5).join(', ')} +${names.length - 5} more`;
}

function formatRotowireReviewReasonsFromErrors(errors = []) {
  const reasons = [];
  for (const e of errors) {
    if (e.code === 'starter_count_not_11') {
      reasons.push(
        `Rotowire has ${e.scraped_count} scraped starters for the ${sideLabel(e.side)} side; expected 11.`
      );
    } else if (e.code === 'matched_count_not_11') {
      reasons.push(
        `Only ${e.matched_count} ${sideLabel(e.side)} starters matched the synced roster; expected 11.`
      );
    } else if (e.code === 'player_no_confident_match') {
      reasons.push(`No confident roster match for: ${playerNames(e.players)}.`);
    } else if (e.code === 'player_ambiguous_match') {
      reasons.push(`Ambiguous similar-name match for: ${playerNames(e.players)}.`);
    }
  }
  return reasons;
}

function formatRotowireManualReasons(err) {
  const detail = err?.response?.data?.detail;
  if (!detail || typeof detail !== 'object') return [formatApiError(err)];
  const details = detail.details || {};

  if (detail.code === 'team_no_confident_match') {
    const best = details.best_candidates?.[0];
    if (best) {
      return [
        `Could not confidently match fixture teams to Rotowire. Best candidate was ${best.away_abbr || best.away || '?'} @ ${best.home_abbr || best.home || '?'} with score ${best.score}.`,
      ];
    }
    return ['Could not confidently match fixture teams to any Rotowire game.'];
  }
  if (detail.code === 'team_ambiguous_match') {
    return ['Multiple similar Rotowire games matched this fixture, so the correct teams were ambiguous.'];
  }

  const reasons = formatRotowireReviewReasonsFromErrors(details.errors || []);
  return reasons.length ? reasons : [detail.message || formatApiError(err)];
}

function ManualRequiredNotice({ error, result }) {
  const reasons = result
    ? formatRotowireReviewReasonsFromErrors(result.manual_review?.errors || [])
    : formatRotowireManualReasons(error);
  const markedCount = result?.matched?.length || 0;
  return (
    <div className="lineups-manual-required" role="alert">
      <div className="lineups-manual-required-title">
        {result ? 'Manual review needed' : 'Manual lineup required'}
      </div>
      <div>
        {result
          ? `Marked ${markedCount} confident Rotowire starters. Review the unmatched or ambiguous starters manually before saving.`
          : 'Rotowire site sync could not identify this fixture safely. Set the starters manually before saving.'}
      </div>
      <ul>
        {reasons.map((reason, idx) => (
          <li key={`${idx}-${reason}`}>{reason}</li>
        ))}
      </ul>
    </div>
  );
}

/** Align with manual_lineups._normalize_sport_bucket for filter matching. */
function normalizeSportBucket(s) {
  const x = String(s || '')
    .trim()
    .toLowerCase();
  if (x === 'clubsoccer' || x === 'intlsoccer') return 'soccer';
  return x;
}

function fixtureMatchesSport(f, sportFilter) {
  if (!sportFilter) return true;
  return normalizeSportBucket(f.sport) === normalizeSportBucket(sportFilter);
}

function fixtureMatchesLeague(f, leagueFilter) {
  if (!leagueFilter) return true;
  const needle = String(leagueFilter).trim().toLowerCase();
  const hay = String(f.league || '')
    .trim()
    .toLowerCase();
  if (!hay) return false;
  return needle === hay || hay.includes(needle);
}

function normalizeLeagueSlug(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function scrapeLeagueKeys(s) {
  return [s?.optic_league, s?.sleeper_league, s?.league, s?.id]
    .map(normalizeLeagueSlug)
    .filter(Boolean);
}

function scrapeMatchesFixtureLeague(s, fixtureLeague) {
  const fixtureKey = normalizeLeagueSlug(fixtureLeague);
  if (!fixtureKey) return true;
  return scrapeLeagueKeys(s).includes(fixtureKey);
}

function RosterTable({ title, rows, onToggle }) {
  if (!rows?.length) {
    return (
      <div className="lineups-roster-col">
        <h3 className="lineups-roster-title">{title}</h3>
        <p className="subtle">No synced players for this side.</p>
      </div>
    );
  }
  return (
    <div className="lineups-roster-col">
      <h3 className="lineups-roster-title">{title}</h3>
      <div className="lineups-table-wrap">
        <table className="lineups-table">
          <thead>
            <tr>
              <th>In</th>
              <th>Player</th>
              <th>Pos</th>
              <th>Markets</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.sleeper_player_id}-${r.optic_player_id}`}>
                <td>
                  <input
                    type="checkbox"
                    checked={!!r.selected}
                    onChange={() => onToggle(r.sleeper_player_id, r.side)}
                    disabled={r.sleeper_player_id == null}
                  />
                </td>
                <td>{r.sleeper_player_name || r.optic_player_name || '—'}</td>
                <td>{r.optic_player_position || '—'}</td>
                <td>{r.n_markets ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LineupsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [fixtureId, setFixtureId] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [leagueFilter, setLeagueFilter] = useState('');
  const [rotowireScrapeId, setRotowireScrapeId] = useState('');
  const [homeRows, setHomeRows] = useState([]);
  const [awayRows, setAwayRows] = useState([]);

  const { data: configData } = useConfig();

  const { data: elig, isLoading: eligLoading } = useQuery({
    queryKey: ['manual-lineups', 'eligible'],
    queryFn: fetchManualLineupEligibleFixtures,
  });

  const allFixtures = useMemo(() => elig?.fixtures || [], [elig]);

  const sportOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const f of allFixtures) {
      const s = f.sport != null && String(f.sport).trim() !== '' ? String(f.sport).trim() : null;
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return out;
  }, [allFixtures]);

  const leagueOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const f of allFixtures) {
      if (!fixtureMatchesSport(f, sportFilter)) continue;
      const lg = f.league != null && String(f.league).trim() !== '' ? String(f.league).trim() : null;
      if (!lg || seen.has(lg)) continue;
      seen.add(lg);
      out.push(lg);
    }
    out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return out;
  }, [allFixtures, sportFilter]);

  const fixtures = useMemo(
    () =>
      allFixtures.filter(
        (f) => fixtureMatchesSport(f, sportFilter) && fixtureMatchesLeague(f, leagueFilter)
      ),
    [allFixtures, sportFilter, leagueFilter]
  );

  const rotowireScrapeOptions = useMemo(
    () => (configData?.rotowire?.scrape?.syncs || []).filter((s) => s?.enabled !== false),
    [configData]
  );

  const selectedFixture = useMemo(
    () => allFixtures.find((f) => f.game_id === fixtureId) || null,
    [allFixtures, fixtureId]
  );

  const matchingRotowireScrapeOptions = useMemo(() => {
    if (!selectedFixture) return rotowireScrapeOptions;
    return rotowireScrapeOptions.filter((s) => scrapeMatchesFixtureLeague(s, selectedFixture.league));
  }, [rotowireScrapeOptions, selectedFixture]);

  useEffect(() => {
    if (leagueFilter && !leagueOptions.includes(leagueFilter)) {
      setLeagueFilter('');
    }
  }, [leagueOptions, leagueFilter]);

  useEffect(() => {
    if (!fixtureId) return;
    const still = fixtures.some((f) => f.game_id === fixtureId);
    if (!still) setFixtureId('');
  }, [fixtureId, fixtures]);

  useEffect(() => {
    if (!matchingRotowireScrapeOptions.length) {
      setRotowireScrapeId('');
      return;
    }
    if (
      !rotowireScrapeId ||
      !matchingRotowireScrapeOptions.some((s) => s.id === rotowireScrapeId)
    ) {
      setRotowireScrapeId(matchingRotowireScrapeOptions[0].id || '');
    }
  }, [matchingRotowireScrapeOptions, rotowireScrapeId]);

  const {
    data: roster,
    isLoading: rosterLoading,
    isError: rosterError,
    error: rosterErr,
  } = useQuery({
    queryKey: ['manual-lineups', 'roster', fixtureId],
    queryFn: () => fetchManualLineupRoster(fixtureId),
    enabled: !!fixtureId,
  });

  useEffect(() => {
    if (!roster) return;
    setHomeRows((roster.roster_home || []).map((r) => ({ ...r })));
    setAwayRows((roster.roster_away || []).map((r) => ({ ...r })));
  }, [roster]);

  const toggle = useCallback((sleeperPlayerId, side) => {
    if (sleeperPlayerId == null) return;
    const setter = side === 'home' ? setHomeRows : setAwayRows;
    setter((prev) =>
      prev.map((r) =>
        r.sleeper_player_id === sleeperPlayerId ? { ...r, selected: !r.selected } : r
      )
    );
  }, []);

  const saveMutation = useMutation({
    mutationFn: ({ fid, players }) => commitManualLineup(fid, { players, source: 'ui', committed: true }),
    onSuccess: (_data, { fid }) => {
      queryClient.invalidateQueries({ queryKey: ['manual-lineups', 'roster', fid] });
    },
  });
  const unsetMutation = useMutation({
    mutationFn: ({ fid }) => commitManualLineup(fid, { players: [], source: 'ui', committed: false }),
    onSuccess: (_data, { fid }) => {
      queryClient.invalidateQueries({ queryKey: ['manual-lineups', 'roster', fid] });
    },
  });
  const rotowireSiteMutation = useMutation({
    mutationFn: ({ fid, scrapeId }) => previewRotowireSiteLineup(fid, { scrape_id: scrapeId }),
    onSuccess: (data) => {
      const selected = new Set((data?.selected_sleeper_ids || []).map((id) => String(id)));
      const applySelection = (rows) =>
        rows.map((r) => ({
          ...r,
          selected: r.sleeper_player_id != null && selected.has(String(r.sleeper_player_id)),
        }));
      setHomeRows((prev) => applySelection(prev));
      setAwayRows((prev) => applySelection(prev));
    },
  });

  const handleSave = useCallback(() => {
    if (!fixtureId) return;
    const players = [
      ...homeRows.map((r) => ({
        sleeper_player_id: r.sleeper_player_id,
        side: 'home',
        selected: !!r.selected,
      })),
      ...awayRows.map((r) => ({
        sleeper_player_id: r.sleeper_player_id,
        side: 'away',
        selected: !!r.selected,
      })),
    ].filter((p) => p.sleeper_player_id != null);
    saveMutation.mutate({ fid: fixtureId, players });
  }, [fixtureId, homeRows, awayRows, saveMutation]);

  const selectFixture = (e) => {
    saveMutation.reset();
    unsetMutation.reset();
    rotowireSiteMutation.reset();
    setFixtureId(e.target.value);
  };

  const selectedRotowireScrape = matchingRotowireScrapeOptions.find(
    (s) => s.id === rotowireScrapeId
  );

  return (
    <div className="lineups-page">
      <div className="page-header">
        <div>
          <h1>Manual lineups</h1>
          <p className="subtle" style={{ margin: '0.35rem 0 0' }}>
            Select a scheduled fixture, mark players you treat as in the eligible set, then save. Completed
            fixtures are excluded from this list. Lineups are stored in Postgres and apply across service
            instances (same pattern as Rotowire history).
          </p>
        </div>
      </div>

      <div className="card">
        <div className="lineups-filters">
          <div className="lineups-filter-field">
            <label className="input-label" htmlFor="lineups-sport">
              Sport
            </label>
            <select
              id="lineups-sport"
              className="lineups-select lineups-select-compact"
              value={sportFilter}
              onChange={(e) => {
                setSportFilter(e.target.value);
                setLeagueFilter('');
              }}
              disabled={eligLoading}
            >
              <option value="">All sports</option>
              {sportOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="lineups-filter-field">
            <label className="input-label" htmlFor="lineups-league">
              League
            </label>
            <select
              id="lineups-league"
              className="lineups-select lineups-select-compact"
              value={leagueFilter}
              onChange={(e) => setLeagueFilter(e.target.value)}
              disabled={eligLoading || leagueOptions.length === 0}
            >
              <option value="">All leagues</option>
              {leagueOptions.map((lg) => (
                <option key={lg} value={lg}>
                  {lg}
                </option>
              ))}
            </select>
          </div>
        </div>
        <label className="input-label" htmlFor="lineups-fixture" style={{ marginTop: '1rem' }}>
          Fixture
        </label>
        <select
          id="lineups-fixture"
          className="lineups-select"
          value={fixtureId}
          onChange={selectFixture}
          disabled={eligLoading}
        >
          <option value="">— Choose fixture —</option>
          {fixtures.map((f) => {
            const id = f.game_id;
            if (!id) return null;
            const ha = f.home_team_abbr || '';
            const aa = f.away_team_abbr || '';
            const matchup = aa && ha ? `${aa} @ ${ha}` : `${f.away_team || '?'} @ ${f.home_team || '?'}`;
            const parts = [matchup];
            if (!sportFilter && f.sport) parts.push(String(f.sport));
            if (!leagueFilter && f.league) parts.push(String(f.league));
            parts.push(formatKickoff(f.start_time_utc));
            const label = parts.join(' · ');
            return (
              <option key={id} value={id}>
                {label}
              </option>
            );
          })}
        </select>
        <div className="lineups-scrape-settings">
          <div className="lineups-filter-field">
            <label className="input-label" htmlFor="lineups-rotowire-scrape">
              Rotowire site sync setting
            </label>
            <select
              id="lineups-rotowire-scrape"
              className="lineups-select lineups-select-compact"
              value={rotowireScrapeId}
              onChange={(e) => {
                rotowireSiteMutation.reset();
                setRotowireScrapeId(e.target.value);
              }}
              disabled={!fixtureId || matchingRotowireScrapeOptions.length === 0}
            >
              {matchingRotowireScrapeOptions.length === 0 ? (
                <option value="">No configured scraper for fixture league</option>
              ) : (
                matchingRotowireScrapeOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label || s.id} ({s.league})
                  </option>
                ))
              )}
            </select>
          </div>
          <p className="subtle">
            {selectedRotowireScrape
              ? `Configured in YAML as rotowire.scrape.syncs.${selectedRotowireScrape.id}; matching requires ${selectedRotowireScrape.expected_starters_per_side || 11} starters per side.`
              : 'Add a rotowire.scrape.syncs entry in YAML to enable site sync for this league.'}
          </p>
        </div>
      </div>

      {fixtureId && rosterLoading && (
        <div className="card subtle">Loading roster…</div>
      )}
      {fixtureId && rosterError && (
        <div className="card" style={{ color: '#f87171' }}>
          {formatApiError(rosterErr)}
        </div>
      )}
      {fixtureId && roster && !rosterLoading && (
        <div className="card">
          <div className="lineups-roster-grid">
            <RosterTable title={`Home (${roster.home_team_abbr || '—'})`} rows={homeRows} onToggle={toggle} />
            <RosterTable title={`Away (${roster.away_team_abbr || '—'})`} rows={awayRows} onToggle={toggle} />
          </div>
          <div className="lineups-actions">
            <button
              type="button"
              className="button button-primary"
              onClick={handleSave}
              disabled={saveMutation.isPending || unsetMutation.isPending || rotowireSiteMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save lineup'}
            </button>
            <button
              type="button"
              className="button button-secondary"
              onClick={() =>
                rotowireSiteMutation.mutate({ fid: fixtureId, scrapeId: rotowireScrapeId })
              }
              disabled={
                !rotowireScrapeId ||
                saveMutation.isPending ||
                unsetMutation.isPending ||
                rotowireSiteMutation.isPending
              }
            >
              {rotowireSiteMutation.isPending ? 'Syncing…' : 'Sync from Rotowire site'}
            </button>
            <button
              type="button"
              className="button button-secondary"
              onClick={() => unsetMutation.mutate({ fid: fixtureId })}
              disabled={
                !roster.has_committed_lineup ||
                saveMutation.isPending ||
                unsetMutation.isPending ||
                rotowireSiteMutation.isPending
              }
            >
              {unsetMutation.isPending ? 'Unsetting…' : 'Unset lineup'}
            </button>
            {roster.has_committed_lineup && (
              <span className="subtle" style={{ marginLeft: '1rem' }}>
                Committed lineup on file
                {user?.email ? ` · ${user.email}` : ''}
              </span>
            )}
            {saveMutation.isSuccess && (
              <span className="subtle" style={{ marginLeft: '1rem', color: 'var(--sleeper-success)' }}>
                Saved.
              </span>
            )}
            {saveMutation.isError && (
              <span style={{ marginLeft: '1rem', color: '#f87171' }}>
                {formatApiError(saveMutation.error)}
              </span>
            )}
            {rotowireSiteMutation.isSuccess && (
              <>
                <span className="subtle" style={{ marginLeft: '1rem', color: 'var(--sleeper-success)' }}>
                  Marked {rotowireSiteMutation.data?.matched?.length || 0} confident Rotowire starters.
                </span>
                {rotowireSiteMutation.data?.needs_manual_review && (
                  <ManualRequiredNotice result={rotowireSiteMutation.data} />
                )}
              </>
            )}
            {rotowireSiteMutation.isError && (
              <ManualRequiredNotice error={rotowireSiteMutation.error} />
            )}
            {unsetMutation.isSuccess && (
              <span className="subtle" style={{ marginLeft: '1rem', color: 'var(--sleeper-success)' }}>
                Lineup unset.
              </span>
            )}
            {unsetMutation.isError && (
              <span style={{ marginLeft: '1rem', color: '#f87171' }}>
                {formatApiError(unsetMutation.error)}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LineupsPage;
